/**
 * useLiveTour.ts — 매물 라이브 투어 상태 관리
 * Firebase RTDB land-tours/{listingId}/isLive 를 실시간 구독
 */

import { useEffect, useState, useCallback } from 'react';
import { ref, onValue, set, off, push, onChildAdded } from 'firebase/database';
import { db } from '../../services/rtdb';

export interface ChatMessage {
  id: string;
  sender: 'broker' | 'viewer';
  text: string;
  ts: number;
}

export interface UseLiveTourReturn {
  isLive: boolean;
  viewerCount: number;
  chatMessages: ChatMessage[];
  // 중개사용
  startLive: () => Promise<void>;
  endLive:   () => Promise<void>;
  // 시청자용
  joinAsViewer: () => void;
  leaveAsViewer: () => void;
  // 채팅
  sendChat: (text: string, role: 'broker' | 'viewer') => void;
}

const RTDB_PATH = 'land-tours';

export function useLiveTour(listingId: string): UseLiveTourReturn {
  const [isLive, setIsLive]             = useState(false);
  const [viewerCount, setViewerCount]   = useState(0);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);

  const base = `${RTDB_PATH}/${listingId}`;

  // 실시간 구독
  useEffect(() => {
    if (!listingId) return;

    const liveRef    = ref(db, `${base}/isLive`);
    const countRef   = ref(db, `${base}/viewerCount`);
    const chatRef    = ref(db, `${base}/chat`);

    const unsubLive  = onValue(liveRef,  snap => setIsLive(snap.val() === true));
    const unsubCount = onValue(countRef, snap => setViewerCount(snap.val() ?? 0));
    const unsubChat  = onChildAdded(chatRef, snap => {
      const msg = snap.val() as Omit<ChatMessage, 'id'> | null;
      if (msg) setChatMessages(prev => [...prev, { ...msg, id: snap.key ?? Date.now().toString() }]);
    });

    return () => {
      off(liveRef);
      off(countRef);
      off(chatRef);
      unsubLive();
      unsubCount();
      unsubChat();
    };
  }, [listingId, base]);

  const startLive = useCallback(async () => {
    await set(ref(db, `${base}/isLive`), true);
    await set(ref(db, `${base}/startedAt`), Date.now());
    await set(ref(db, `${base}/viewerCount`), 0);
  }, [base]);

  const endLive = useCallback(async () => {
    await set(ref(db, `${base}/isLive`), false);
    await set(ref(db, `${base}/endedAt`), Date.now());
  }, [base]);

  const joinAsViewer = useCallback(() => {
    const countRef = ref(db, `${base}/viewerCount`);
    onValue(countRef, snap => {
      const cur = snap.val() ?? 0;
      set(countRef, cur + 1);
    }, { onlyOnce: true });
  }, [base]);

  const leaveAsViewer = useCallback(() => {
    const countRef = ref(db, `${base}/viewerCount`);
    onValue(countRef, snap => {
      const cur = snap.val() ?? 1;
      set(countRef, Math.max(0, cur - 1));
    }, { onlyOnce: true });
  }, [base]);

  const sendChat = useCallback((text: string, role: 'broker' | 'viewer') => {
    push(ref(db, `${base}/chat`), { sender: role, text, ts: Date.now() });
  }, [base]);

  return { isLive, viewerCount, chatMessages, startLive, endLive, joinAsViewer, leaveAsViewer, sendChat };
}
